import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { XCircle, Edit3, Layers, History, Mail, AlertTriangle } from 'lucide-react';
import { STLViewerWithControls } from '../components/STLViewer';
import { trackFileUpload, trackSupportGeneration, trackDownload } from '../lib/analytics';
import { EmailVerificationBanner } from '../components/EmailVerificationBanner';

// Import refactored components
import { QuotaDisplay } from '../components/QuotaDisplay';
import { ModeSelection } from '../components/ModeSelection';
import { OrientationControls } from '../components/OrientationControls';
import { FileUploader } from '../components/FileUploader';
import { BatchUploader } from '../components/BatchUploader';
import { BatchResults } from '../components/BatchResults';
import { SupportResults } from '../components/SupportResults';
import { SupportPointEditor } from '../components/SupportPointEditor';

// Default filament prices (USD per kg)
const DEFAULT_PRICES = {
  PLA: 20,
  ABS: 22,
  PETG: 25,
  TPU: 35,
  PVA: 60,
  HIPS: 25,
};

// Default support preferences
const DEFAULT_SUPPORT_PREFERENCES = {
  tree_style: 'forest',
  lattice_spacing: 2.0,
  lattice_width: 0.6,
  optimize_orientation: true,
  density: 'standard',
  hollow_supports: true,
  wall_thickness: 0.8,
  tapered_tips: true
};

// Helper function to extract error message from API responses
const extractErrorMessage = (err, fallbackMessage = 'An error occurred') => {
  if (err.response?.data?.detail) {
    const detail = err.response.data.detail;
    if (Array.isArray(detail)) {
      return detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
    }
    if (typeof detail === 'string') {
      return detail;
    }
    if (detail.msg || detail.message) {
      return detail.msg || detail.message;
    }
    return JSON.stringify(detail);
  }
  if (err.message && typeof err.message === 'string') {
    return err.message;
  }
  return fallbackMessage;
};

const SmartSupport = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // File state
  const [file, setFile] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  
  // Processing state
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [supportResult, setSupportResult] = useState(null);
  const [error, setError] = useState('');
  
  // Progress tracking state
  const [progressInfo, setProgressInfo] = useState({
    stage: '',
    percent: 0,
    elapsedTime: 0,
    estimatedTotal: 45 // Default estimate in seconds
  });
  
  // Quota state
  const [quota, setQuota] = useState({ used: 0, limit: 1, remaining: 1 });
  
  // Generation settings
  const [optimizeOrientation, setOptimizeOrientation] = useState(true);
  const [manualRotation, setManualRotation] = useState({ x: 0, y: 0, z: 0 });
  const [mode, setMode] = useState('pro');
  const [supportType, setSupportType] = useState('tree');
  const [treeStyle, setTreeStyle] = useState('forest');
  const [density, setDensity] = useState('standard');
  const [latticeSpacing, setLatticeSpacing] = useState(2.0);
  const [latticeWidth, setLatticeWidth] = useState(0.6);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  
  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrls, setPreviewUrls] = useState({ model: null, support: null });
  
  // Support Point Editor state
  const [showPointEditor, setShowPointEditor] = useState(false);
  
  // Upload mode state
  const [uploadMode, setUploadMode] = useState('single');
  
  // Batch processing state
  const [batchFiles, setBatchFiles] = useState([]);
  const [batchJob, setBatchJob] = useState(null);
  const [batchProcessing, setBatchProcessing] = useState(false);
  
  // Custom prices state
  const [customPrices, setCustomPrices] = useState(DEFAULT_PRICES);
  const [pricesLoaded, setPricesLoaded] = useState(false);
  
  // Preferences state
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [savingPreferences, setSavingPreferences] = useState(false);

  // Fetch quota, prices, and preferences on mount
  useEffect(() => {
    fetchQuota();
    fetchUserPrices();
    fetchSupportPreferences();
  }, []);

  // Cleanup blob URL on unmount or file change
  useEffect(() => {
    return () => {
      if (previewFile) {
        URL.revokeObjectURL(previewFile);
      }
    };
  }, [previewFile]);

  const fetchQuota = async () => {
    try {
      const response = await api.get('/supports/quota');
      setQuota(response.data);
    } catch (err) {
      console.error('Failed to fetch quota');
    }
  };

  const fetchUserPrices = async () => {
    try {
      const response = await api.get('/user/preferences/filament-prices');
      if (response.data.prices) {
        setCustomPrices(response.data.prices);
      }
      setPricesLoaded(true);
    } catch (err) {
      console.error('Failed to fetch user prices:', err);
      setPricesLoaded(true);
    }
  };

  const fetchSupportPreferences = async () => {
    try {
      const response = await api.get('/user/preferences/support-settings');
      if (response.data.preferences) {
        const prefs = response.data.preferences;
        // Apply saved preferences to state
        if (prefs.tree_style) setTreeStyle(prefs.tree_style);
        if (prefs.lattice_spacing !== undefined) setLatticeSpacing(prefs.lattice_spacing);
        if (prefs.lattice_width !== undefined) setLatticeWidth(prefs.lattice_width);
        if (prefs.optimize_orientation !== undefined) setOptimizeOrientation(prefs.optimize_orientation);
      }
      setPreferencesLoaded(true);
    } catch (err) {
      console.error('Failed to fetch support preferences:', err);
      setPreferencesLoaded(true);
    }
  };

  const saveSupportPreferences = async (newPrefs) => {
    setSavingPreferences(true);
    try {
      await api.put('/user/preferences/support-settings', newPrefs);
    } catch (err) {
      console.error('Failed to save support preferences:', err);
    } finally {
      setSavingPreferences(false);
    }
  };

  // Auto-save preferences when they change
  const handleTreeStyleChange = (value) => {
    setTreeStyle(value);
    saveSupportPreferences({ tree_style: value });
  };

  const handleLatticeSpacingChange = (value) => {
    setLatticeSpacing(value);
    saveSupportPreferences({ lattice_spacing: value });
  };

  const handleLatticeWidthChange = (value) => {
    setLatticeWidth(value);
    saveSupportPreferences({ lattice_width: value });
  };

  const handleOptimizeOrientationChange = (value) => {
    setOptimizeOrientation(value);
    saveSupportPreferences({ optimize_orientation: value });
  };

  // File selection handler
  const handleFileSelect = (selectedFile) => {
    if (selectedFile && selectedFile.name.toLowerCase().endsWith('.stl')) {
      setFile(selectedFile);
      setError('');
      setSupportResult(null);
      
      const url = URL.createObjectURL(selectedFile);
      setPreviewFile(url);
      setManualRotation({ x: 0, y: 0, z: 0 });
    } else {
      setError('Please select a valid STL file');
      setFile(null);
      setPreviewFile(null);
    }
  };

  // Batch file selection handler
  const handleBatchFileSelect = (validFiles) => {
    if (validFiles.length === 0) {
      setError('Please select valid STL files');
      return;
    }
    if (validFiles.length > 20) {
      setError('Maximum 20 files per batch');
      return;
    }
    setBatchFiles(validFiles);
    setError('');
    setBatchJob(null);
  };

  // Generate supports with custom points from editor
  const handleGenerateWithCustomPoints = async (customPoints) => {
    if (!file || customPoints.length === 0) return;
    
    setShowPointEditor(false);
    setUploading(true);
    setProcessing(true);
    setError('');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('custom_points', JSON.stringify(customPoints));
      formData.append('tree_style', treeStyle);
      formData.append('rotation_x', optimizeOrientation ? 0 : manualRotation.x);
      formData.append('rotation_y', optimizeOrientation ? 0 : manualRotation.y);
      formData.append('rotation_z', optimizeOrientation ? 0 : manualRotation.z);
      formData.append('lattice_spacing', latticeSpacing.toString());
      formData.append('lattice_width', latticeWidth.toString());
      
      const response = await api.post('/supports/generate-with-custom-points', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000, // 2 minute timeout for custom points
      });
      
      const { support_id } = response.data;
      
      // Use polling for custom points too
      pollSupportStatus(support_id);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to generate supports with custom points'));
      setUploading(false);
      setProcessing(false);
    }
  };

  // Upload handler for single file
  const handleUpload = async () => {
    if (!file) return;
    if (user && !user.is_verified) {
      setError('Please verify your email before generating supports.');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('optimize_orientation', optimizeOrientation);
      
      if (!optimizeOrientation) {
        formData.append('manual_rotation_x', manualRotation.x);
        formData.append('manual_rotation_y', manualRotation.y);
        formData.append('manual_rotation_z', manualRotation.z);
      }
      
      formData.append('mode', mode);
      formData.append('support_type', supportType);
      formData.append('tree_style', treeStyle);
      formData.append('lattice_spacing', latticeSpacing.toString());
      formData.append('lattice_width', latticeWidth.toString());

      const response = await api.post('/supports/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      trackFileUpload('stl', file.size);
      trackSupportGeneration(mode, treeStyle);

      const { support_id } = response.data;
      setProcessing(true);
      pollSupportStatus(support_id);
    } catch (err) {
      setError(extractErrorMessage(err, 'Upload failed'));
      setUploading(false);
    }
  };

  const pollSupportStatus = async (supportId) => {
    // Extended timeout: 450 attempts x 2s = 15 minutes. Complex models on
    // production hardware can legitimately take 5-10 minutes to generate
    // supports (ray-casting + lattice interface + branch routing). We'd
    // rather wait longer than falsely time out a legitimate job.
    const maxAttempts = 450;
    let attempts = 0;
    let networkErrors = 0;
    const maxNetworkErrors = 20; // ~40s of consecutive failures before giving up
    const startTime = Date.now();
    
    // Progress stages for user feedback — stretched to reflect real
    // production generation times (complex models can take 5-10 minutes).
    const progressStages = [
      { time: 0, stage: 'Uploading model...', percent: 5 },
      { time: 5, stage: 'Analyzing geometry...', percent: 12 },
      { time: 15, stage: 'Detecting overhangs...', percent: 22 },
      { time: 30, stage: 'Calculating support points...', percent: 32 },
      { time: 50, stage: 'Building tree structure...', percent: 45 },
      { time: 80, stage: 'Generating lattice interface...', percent: 58 },
      { time: 120, stage: 'Creating branch connections...', percent: 70 },
      { time: 170, stage: 'Optimizing mesh...', percent: 80 },
      { time: 230, stage: 'Refining geometry...', percent: 87 },
      { time: 300, stage: 'Almost done...', percent: 92 },
      { time: 420, stage: 'Finalizing supports...', percent: 96 },
    ];

    const poll = setInterval(async () => {
      try {
        const response = await api.get(`/supports/${supportId}`);
        const job = response.data;
        networkErrors = 0; // Reset network error counter on success
        
        // Calculate elapsed time
        const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
        
        // Update progress based on elapsed time
        let currentStage = progressStages[0];
        for (const stage of progressStages) {
          if (elapsedSeconds >= stage.time) {
            currentStage = stage;
          }
        }
        
        setProgressInfo({
          stage: currentStage.stage,
          percent: Math.min(currentStage.percent, 95),
          elapsedTime: elapsedSeconds,
          estimatedTotal: treeStyle === 'hierarchical' ? 60 : 45
        });

        if (job.status === 'completed') {
          clearInterval(poll);
          setProgressInfo(prev => ({ ...prev, stage: 'Complete!', percent: 100 }));
          // Fetch the FULL record (with contact_points, analysis, etc.) now
          // that status is 'completed'. Polling uses the lightweight endpoint.
          try {
            const fullResponse = await api.get(`/supports/${supportId}?full=true`);
            setSupportResult(fullResponse.data);
          } catch (fullErr) {
            // Fallback to lightweight record if full fetch fails
            setSupportResult(job);
          }
          setUploading(false);
          setProcessing(false);
          fetchQuota();
        } else if (job.status === 'failed') {
          clearInterval(poll);
          setError(job.error_message || 'Support generation failed');
          setUploading(false);
          setProcessing(false);
        }

        attempts++;
        
        if (attempts >= maxAttempts) {
          clearInterval(poll);
          setError('Support generation timed out after 15 minutes. Complex models may take longer - please try a simpler model or contact support.');
          setUploading(false);
          setProcessing(false);
        }
      } catch (err) {
        networkErrors++;
        attempts++;
        
        // Calculate elapsed time for progress display even on errors
        const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
        let currentStage = progressStages[0];
        for (const stage of progressStages) {
          if (elapsedSeconds >= stage.time) {
            currentStage = stage;
          }
        }
        setProgressInfo({
          stage: `${currentStage.stage} (processing...)`,
          percent: Math.min(currentStage.percent, 95),
          elapsedTime: elapsedSeconds,
          estimatedTotal: treeStyle === 'hierarchical' ? 60 : 45
        });
        
        // Only give up after too many consecutive network errors or max attempts
        if (networkErrors >= maxNetworkErrors || attempts >= maxAttempts) {
          clearInterval(poll);
          setError(`Support generation failed: ${extractErrorMessage(err, 'Lost connection to server')}`);
          setUploading(false);
          setProcessing(false);
        }
        // Otherwise, keep polling - the server may still be processing
      }
    }, 2000);
  };

  // Batch upload handler
  const handleBatchUpload = async () => {
    if (batchFiles.length === 0) return;
    
    setBatchProcessing(true);
    setError('');
    
    try {
      const formData = new FormData();
      batchFiles.forEach(file => {
        formData.append('files', file);
      });
      formData.append('optimize_orientation', optimizeOrientation);
      formData.append('mode', mode);
      formData.append('support_type', supportType);
      formData.append('tree_style', treeStyle);
      formData.append('density', density);
      formData.append('hollow_supports', 'true');
      formData.append('wall_thickness', '0.8');
      formData.append('tapered_tips', 'true');
      formData.append('lattice_spacing', latticeSpacing.toString());
      formData.append('lattice_width', latticeWidth.toString());
      
      const response = await api.post('/supports/batch/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      if (response.data.batch_id) {
        await pollBatchStatus(response.data.batch_id);
      }
    } catch (err) {
      setError(extractErrorMessage(err, 'Batch upload failed'));
    } finally {
      setBatchProcessing(false);
    }
  };

  const pollBatchStatus = async (batchId) => {
    try {
      const response = await api.get(`/supports/batch/${batchId}`);
      setBatchJob(response.data);
      
      if (response.data.status === 'processing' || response.data.status === 'pending') {
        setTimeout(() => pollBatchStatus(batchId), 2000);
      }
    } catch (err) {
      setError('Failed to get batch status');
    }
  };

  const handleBatchDownload = async () => {
    if (!batchJob || !batchJob.id) return;
    
    try {
      const response = await api.get(`/supports/batch/${batchJob.id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `batch_${batchJob.id}_supports.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download batch ZIP');
    }
  };

  const resetBatch = () => {
    setBatchFiles([]);
    setBatchJob(null);
    setError('');
  };

  // Download handler
  const handleDownload = async (format) => {
    if (!supportResult) {
      alert('No support result available');
      return;
    }

    trackDownload(format, 'generate-supports');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        return;
      }
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const downloadUrl = `${backendUrl}/api/supports/${supportResult.id}/download/${format}?token=${token}`;
      
      console.log('[Download] Attempting download from:', downloadUrl);
      
      const xhr = new XMLHttpRequest();
      xhr.open('GET', downloadUrl, true);
      xhr.responseType = 'blob';
      
      xhr.onload = function() {
        if (xhr.status === 200) {
          const blob = xhr.response;
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          
          let extension = 'stl';
          let suffix = '_with_supports';
          
          if (format === 'gcode') {
            extension = 'gcode';
            suffix = '';
          } else if (format === '3mf') {
            extension = '3mf';
            suffix = '_with_supports';
          } else if (format === 'support-only') {
            extension = 'stl';
            suffix = '_supports_only';
          } else if (format === 'model-only') {
            extension = 'stl';
            suffix = '_model_only';
          }
          
          a.download = supportResult.original_filename.replace('.stl', `${suffix}.${extension}`);
          document.body.appendChild(a);
          a.click();
          setTimeout(() => {
            URL.revokeObjectURL(url);
            document.body.removeChild(a);
          }, 100);
        } else {
          // Try to parse error message from response
          const reader = new FileReader();
          reader.onload = function() {
            try {
              const errorData = JSON.parse(reader.result);
              setError(`Download failed: ${errorData.detail || 'Unknown error'}`);
            } catch (e) {
              setError(`Download failed (HTTP ${xhr.status})`);
            }
          };
          reader.readAsText(xhr.response);
        }
      };
      
      xhr.onerror = function() {
        console.error('[Download] XHR error event triggered');
        setError('Network error during download. Please check your connection and try again.');
      };
      
      xhr.send();
    } catch (err) {
      console.error('Download error:', err);
      setError(`Download failed: ${err.message}`);
    }
  };

  // Preview handler
  const handlePreview = async () => {
    if (!supportResult) {
      alert('No support result available');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        return;
      }
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      const modelUrl = `${backendUrl}/api/supports/${supportResult.id}/download/model-only?token=${token}`;
      
      // Support-only may not be available for older generations
      // Only include if support_only_file_id is set
      const supportOnlyUrl = supportResult.support_only_available !== false 
        ? `${backendUrl}/api/supports/${supportResult.id}/download/support-only?token=${token}`
        : null;
      
      setPreviewUrls({
        model: modelUrl,
        support: supportOnlyUrl
      });
      setShowPreview(true);
    } catch (err) {
      console.error('Preview error:', err);
      setError(`Preview failed: ${err.message}`);
    }
  };

  // Reset handler
  const handleReset = () => {
    setFile(null);
    setSupportResult(null);
    setError('');
    setPreviewFile(null);
  };

  // Handle regeneration with new settings
  const handleRegenerate = async (newSettings) => {
    if (!file) return;
    
    // Update settings if provided
    if (newSettings.treeStyle) {
      setTreeStyle(newSettings.treeStyle);
    }
    if (newSettings.density) {
      setDensity(newSettings.density);
    }
    
    // Clear current result and re-upload
    setSupportResult(null);
    setError('');
    
    // Trigger new upload with updated settings
    setTimeout(() => {
      handleUpload();
    }, 100);
  };

  // Get current settings for regenerate component
  const currentSettings = {
    treeStyle,
    density,
    hollowSupports: true,
    mode,
    latticeSpacing,
    latticeWidth
  };

  return (
    <div className="max-w-4xl" data-testid="smart-support-page">
      {/* Email Verification Gate */}
      {user && !user.is_verified && (
        <EmailVerificationBanner />
      )}

      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Smart Supports (Beta)</h1>
          <p className="text-text-secondary">
            Upload an STL file to generate AI-optimized support structures for better print quality
          </p>
        </div>
        <button
          onClick={() => navigate('/dashboard/support-history')}
          className="px-4 py-2 bg-bg-light hover:bg-bg-light/80 text-text-secondary hover:text-white rounded-lg transition flex items-center gap-2"
          data-testid="view-history-btn"
        >
          <History className="w-4 h-4" />
          View History
        </button>
      </div>

      {/* Quota Display */}
      <QuotaDisplay quota={quota} />

      {/* Mode Selection - Only show before result */}
      {!supportResult && (
        <ModeSelection
          mode={mode}
          setMode={setMode}
          supportType={supportType}
          setSupportType={setSupportType}
          treeStyle={treeStyle}
          setTreeStyle={handleTreeStyleChange}
          showAdvancedSettings={showAdvancedSettings}
          setShowAdvancedSettings={setShowAdvancedSettings}
          latticeSpacing={latticeSpacing}
          setLatticeSpacing={handleLatticeSpacingChange}
          latticeWidth={latticeWidth}
          setLatticeWidth={handleLatticeWidthChange}
          savingPreferences={savingPreferences}
        />
      )}

      {/* Orientation Controls - Only show before result and batch job */}
      {!supportResult && !batchJob && (
        <OrientationControls
          optimizeOrientation={optimizeOrientation}
          setOptimizeOrientation={handleOptimizeOrientationChange}
          manualRotation={manualRotation}
          setManualRotation={setManualRotation}
          previewFile={previewFile}
          file={file}
        />
      )}

      {/* Upload Mode Tabs */}
      {!supportResult && !batchJob && (
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setUploadMode('single')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              uploadMode === 'single'
                ? 'bg-accent text-white'
                : 'bg-bg-light text-text-secondary hover:text-white'
            }`}
            data-testid="tab-single"
          >
            Single File
          </button>
          <button
            onClick={() => setUploadMode('batch')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              uploadMode === 'batch'
                ? 'bg-accent text-white'
                : 'bg-bg-light text-text-secondary hover:text-white'
            }`}
            data-testid="tab-batch"
          >
            Batch Processing
          </button>
        </div>
      )}

      {/* Single File Upload */}
      {!supportResult && !batchJob && uploadMode === 'single' && !processing && (
        <FileUploader
          file={file}
          onFileSelect={handleFileSelect}
          onUpload={handleUpload}
          onEditPoints={() => setShowPointEditor(true)}
          uploading={uploading}
          processing={processing}
          quotaRemaining={quota.remaining}
        />
      )}

      {/* Progress Indicator During Generation */}
      {processing && (
        <div className="bg-bg-light p-6 rounded-lg border border-primary/30 mb-6" data-testid="progress-indicator">
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-4 relative">
              <div className="absolute inset-0 border-4 border-primary/20 rounded-full"></div>
              <div 
                className="absolute inset-0 border-4 border-primary rounded-full animate-spin"
                style={{ 
                  borderTopColor: 'transparent',
                  borderRightColor: 'transparent',
                }}
              ></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-lg font-bold text-primary">{progressInfo.percent}%</span>
              </div>
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Generating Supports</h3>
            <p className="text-text-secondary">{progressInfo.stage}</p>
          </div>
          
          {/* Progress Bar */}
          <div className="mb-4">
            <div className="h-3 bg-bg-dark rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-500 ease-out"
                style={{ width: `${progressInfo.percent}%` }}
              ></div>
            </div>
          </div>
          
          {/* Time Info */}
          <div className="flex justify-between text-sm text-text-muted">
            <span>Elapsed: {Math.floor(progressInfo.elapsedTime / 60)}:{(progressInfo.elapsedTime % 60).toString().padStart(2, '0')}</span>
            <span>Est. total: ~{progressInfo.estimatedTotal}s</span>
          </div>
          
          {/* Stage Description */}
          <div className="mt-4 p-3 bg-bg-dark/50 rounded-lg">
            <p className="text-xs text-text-secondary">
              <span className="text-primary font-medium">Tip:</span> {treeStyle === 'hierarchical' 
                ? 'Hierarchical mode creates OrcaSlicer-style organic supports with fewer branches.'
                : 'Forest mode creates multiple independent trees for comprehensive support coverage.'}
            </p>
          </div>
        </div>
      )}

      {/* Batch Upload */}
      {!supportResult && !batchJob && uploadMode === 'batch' && (
        <BatchUploader
          batchFiles={batchFiles}
          onBatchFileSelect={handleBatchFileSelect}
          onBatchUpload={handleBatchUpload}
          onClear={() => setBatchFiles([])}
          batchProcessing={batchProcessing}
        />
      )}

      {/* Batch Results */}
      {batchJob && (
        <BatchResults
          batchJob={batchJob}
          onDownload={handleBatchDownload}
          onReset={resetBatch}
        />
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 p-4 rounded-lg mb-6 flex items-center gap-3" data-testid="error-message">
          <XCircle className="w-5 h-5 text-red-400" />
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Support Results */}
      {supportResult && supportResult.ai_explanation && (
        <SupportResults
          supportResult={supportResult}
          mode={mode}
          onDownload={handleDownload}
          onPreview={handlePreview}
          onReset={handleReset}
          onRegenerate={handleRegenerate}
          currentSettings={currentSettings}
          customPrices={customPrices}
          user={user}
        />
      )}

      {/* 3D Preview Modal */}
      {showPreview && previewUrls.model && (
        <STLViewerWithControls
          modelUrl={previewUrls.model}
          supportUrl={previewUrls.support}
          onClose={() => setShowPreview(false)}
          contactPoints={supportResult?.contact_points || []}
        />
      )}

      {/* Support Point Editor Modal */}
      {showPointEditor && file && (
        <SupportPointEditor
          file={file}
          treeStyle={treeStyle}
          rotation={optimizeOrientation ? null : manualRotation}
          onClose={() => setShowPointEditor(false)}
          onApply={handleGenerateWithCustomPoints}
        />
      )}
    </div>
  );
};

export default SmartSupport;
